import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Box,
  Typography,
  Button,
  Alert,
  Snackbar,
  CircularProgress,
} from "@mui/material";
import { Save as SaveIcon } from "@mui/icons-material";
import LockRoundedIcon from "@mui/icons-material/LockRounded";
import axios from "axios";
import { AutoTaskAuthContext } from "../../Context/AutoTaskContext";
import { PawnModeContext } from "../../Context/PawnModeContext";
import { Preferences } from "@capacitor/preferences";
import unofficialModeIndicatorGIF from "../../assets/icons/unofficial-mode.gif";

import PawnConfiguration from "./PawnConfiguration";
import PawnInterestSlabs from "./PawnInterestSlabs";
import PawnNavButtons from "./pawnNavButtons";
import {
  usePawnEditAccess,
  ViewOnlyZone,
  VIEW_ONLY_MESSAGE,
} from "./pawnEditAccess";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";

/* ─── colour tokens ─────────────────────────────────────────── */
const OFFICIAL_COLOR = "#10b981"; // emerald
const UNOFFICIAL_COLOR = "#f59e0b"; // amber
const BG_PAGE = "#F8FAFC";

/* ─── four-quadrant layout ───────────────────────────────────────
   The four settings sections split the viewport into equal quarters and each
   one scrolls inside its own quadrant, so the PAGE never scrolls: every section
   heading stays visible and reaching the last one is not a scroll to the bottom
   of a very tall column.

   Flex, not `calc(100vh - N)`: this page sits inside App.js's scrolling <main>,
   which already takes 56px of top margin and another 76px of bottom nav below
   md, so a measured constant would only ever line up on one screen size. The
   page claims `height: 100%` of what it was handed and the grid divides it.
   Same convention as the report screens (see reportLayout.js).

   Below `lg` the quarters are abandoned: on a phone or a tablet a quarter of the
   height is a couple of fields behind a scrollbar, so the sections go back to
   full width, natural height, and the page scrolls as it always did.

   CSS Grid rather than MUI's <Grid>: `1fr 1fr` rows divide the leftover height
   into exact halves, where MUI Grid's spacing works by negative margins that
   make four `height: 50%` items add up to taller than their container. */
const QUADRANT_GRID_SX = {
  flex: 1,
  minHeight: 0,
  display: "grid",
  gap: { xs: 2, lg: 2.5 },
  gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" },
  // The two rows are NOT equal halves. Global Master and Account Mapping hold a
  // fixed set of fields and will never hold more, so the top row is sized to
  // its content (`auto`) and every spare pixel goes to the bottom row, where
  // the scheme and slab lists grow without limit.
  //
  // The floors matter in a short window (a laptop with a browser toolbar and a
  // taskbar): the top row keeps 200px so its header and first field row stay
  // visible, the bottom keeps 320px so the lists are still worth reading, and
  // past that the grid scrolls rather than squeezing all four into slivers —
  // the same degradation the report screens use.
  gridTemplateRows: {
    xs: "auto",
    lg: "minmax(200px, auto) minmax(320px, 1fr)",
  },
  // Each quadrant scrolls its own content, so in a window with room this never
  // has anything to scroll — it only engages when the rows have hit that floor.
  // Below lg the rows are `auto`, the sections stand at their natural height,
  // and App.js's <main> is the scroller, as on every other screen.
  overflowY: { xs: "visible", lg: "auto" },
  overflowX: "hidden",
};

// Every quadrant needs minHeight: 0, or a grid row refuses to shrink below its
// content and the internal scroll never engages.
const QUADRANT_SX = {
  minWidth: 0,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
};

const PawnSettings = () => {
  const { currentStoreInfo, userInfo, currentBranch } =
    useContext(AutoTaskAuthContext);
  const storeId = currentStoreInfo?.id;
  const userId = userInfo?.id;
  // Store Staff see the settings but cannot change them (pawnEditAccess.js).
  const canEditSettings = usePawnEditAccess();

  // The financial year is stored per branch (branch_master.current_fy), so both
  // reading it and setting it need to say which branch. null is a real value —
  // the service roles are in every branch and so in none — and the server maps
  // it to the store's main branch.
  const branchId = currentBranch?.id ?? userInfo?.branch_id ?? null;

  // Mode ("official" = 1 / "unofficial" = 2) is owned globally by
  // PawnModeContext and switched via the hidden F12 keyboard gesture.
  const {
    mode: currentStatus,
    modeLoaded,
    isUnofficial,
  } = useContext(PawnModeContext);

  const [accounts, setAccounts] = useState([]);
  const [accountsLoading, setAccountsLoading] = useState(false);

  const [fyOptions, setFyOptions] = useState([]);
  const [fyLoading, setFyLoading] = useState(false);
  const [currentFyId, setCurrentFyId] = useState(null);
  // The year the calendar is in, so the picker can mark it. Comes from the
  // server derived from the date — it replaced the is_active column, which was
  // a stored flag that could be (and was) stale.
  const [calendarFyId, setCalendarFyId] = useState(null);

  const [settings, setSettings] = useState({
    prefix: "",
    use_prefix: false,
    code: "LN",
    next_num: 1,
    master_int: 1.0,
    master_ins: null,
    OTP_for_cust: false,
    allow_duplicate_mobile: false,
    OTP_for_settlement: false,
    manual_voucher_no: false,
    one_month_deduct: false,
    pledge_acc: null,
    cash_acc: null,
    UPI_acc: null,
    interest_acc: null,
    insurance_acc: null,
    other_charges_acc: null,
    no_interest_upto_2_days: false,
    pawn_monthly_interest: false,
    pawn_compound_interest: false,
    less_than_15_days_as_full_month: false,
    less_than_15_days_as_half_month: false,
  });

  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(false);

  // Prefix-change support: what the store currently has saved, a live "peek"
  // of what the typed prefix would do, and the reset-vs-continue choice.
  const [savedPrefix, setSavedPrefix] = useState("");
  const [savedUsePrefix, setSavedUsePrefix] = useState(false);
  const [prefixPeek, setPrefixPeek] = useState(null);
  const [resetSequence, setResetSequence] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const showSnackbar = (message, severity = "success") =>
    setSnackbar({ open: true, message, severity });

  const fetchFinancialYears = useCallback(async () => {
    setFyLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/accounts/financial-year`,
        {
          params: {
            ...(storeId !== null && storeId !== undefined
              ? { store_id: storeId }
              : {}),
            ...(branchId !== null && branchId !== undefined
              ? { branch_id: branchId }
              : {}),
          },
        },
      );
      if (response.data.success) {
        const years = response.data.data;
        setFyOptions(years);
        // Two different questions, which the old is_active column tried to
        // answer with one flag:
        //   branchFyId    the year THIS BRANCH is working in, or null
        //   calendarFyId  the year the calendar is in — same for everyone
        return {
          years,
          branchFyId: response.data.current_fy_id ?? null,
          calendarFyId: response.data.calendar_fy_id ?? null,
        };
      }
      return { years: [], branchFyId: null, calendarFyId: null };
    } catch (error) {
      console.error("Error fetching financial years:", error);
      showSnackbar("Failed to load financial years", "error");
      return { years: [], branchFyId: null, calendarFyId: null };
    } finally {
      setFyLoading(false);
    }
  }, [storeId, branchId]);

  const setActiveFY = useCallback(
    async (fyId) => {
      if (storeId === null || storeId === undefined) {
        showSnackbar("Store not available — cannot set the financial year", "error");
        return false;
      }
      try {
        // Persists onto branch_master.current_fy for this branch, so the choice
        // survives a reload and does not leak into any other branch or store.
        await axios.patch(
          `${API_BASE_URL}/api/accounts/financial-year/${fyId}/set-active`,
          { store_id: storeId, branch_id: branchId ?? null },
        );
        const selectedFY = fyOptions.find((fy) => fy.fy_id === fyId);
        if (selectedFY) {
          await Preferences.set({
            key: "default_fy",
            value: String(selectedFY.fy_year),
          });
          await Preferences.set({
            key: "default_fy_id",
            value: String(selectedFY.fy_id),
          });
        }
        return true;
      } catch (error) {
        console.error("Error setting active financial year:", error);
        showSnackbar(
          error.response?.data?.message || "Failed to set the financial year",
          "error",
        );
        return false;
      }
    },
    [fyOptions, storeId, branchId],
  );

  const fetchSettings = useCallback(async () => {
    if (!storeId) return;
    setFetching(true);
    try {
      const res = await axios.get(
        `${API_BASE_URL}/api/pawn/settings/${storeId}`,
      );
      if (res.data.success && res.data.data) {
        const data = res.data.data;
        setSettings({
          prefix: data.prefix || "",
          use_prefix: Boolean(data.use_prefix),
          master_int: parseFloat(data.master_int) || 1.0,
          master_ins:
            data.master_ins !== null ? parseFloat(data.master_ins) : null,
          OTP_for_cust: Boolean(data.OTP_for_cust),
          allow_duplicate_mobile: Boolean(data.allow_duplicate_mobile),
          OTP_for_settlement: Boolean(data.OTP_for_settlement),
          manual_voucher_no: Boolean(data.manual_voucher_no),
          one_month_deduct: Boolean(data.one_month_deduct),
          pledge_acc: data.pledge_acc || null,
          cash_acc: data.cash_acc || null,
          UPI_acc: data.UPI_acc || null,
          interest_acc: data.interest_acc || null,
          insurance_acc: data.insurance_acc || null,
          other_charges_acc: data.other_charges_acc || null,
          no_interest_upto_2_days: Boolean(data.no_interest_upto_2_days),
          pawn_monthly_interest: Boolean(data.pawn_monthly_interest),
          pawn_compound_interest: Boolean(data.pawn_compound_interest),
          less_than_15_days_as_full_month: Boolean(
            data.less_than_15_days_as_full_month,
          ),
          less_than_15_days_as_half_month: Boolean(
            data.less_than_15_days_as_half_month,
          ),
          next_voucher_no_pay: data.next_voucher_no_pay || 1,
        });
        // Remember the saved prefix namespace so we can detect a change, and
        // clear any stale peek / reset choice from a previous edit.
        setSavedPrefix(data.prefix || "");
        setSavedUsePrefix(Boolean(data.use_prefix));
        setPrefixPeek(null);
        setResetSequence(false);
      }
    } catch (error) {
      showSnackbar("Failed to load settings", "error");
    } finally {
      setFetching(false);
    }
  }, [storeId]);

  // When the typed prefix namespace differs from what's saved, ask the backend
  // what that prefix would do (new? resumes at? reset available?). Debounced.
  useEffect(() => {
    if (!storeId) return;
    const curNs = settings.use_prefix && settings.prefix ? settings.prefix : "";
    const savedNs = savedUsePrefix && savedPrefix ? savedPrefix : "";
    if (curNs === savedNs) {
      setPrefixPeek(null);
      setResetSequence(false);
      return;
    }
    if (currentStatus === 2 && currentFyId === null) return;
    setResetSequence(false); // default to "continue" until the user opts in
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(
          `${API_BASE_URL}/api/pawn/settings/peek-prefix`,
          {
            params: {
              store_id: storeId,
              prefix: settings.prefix || "",
              use_prefix: settings.use_prefix ? 1 : 0,
              mode: currentStatus,
              ...(currentStatus === 2 ? { fy_id: currentFyId } : {}),
            },
          },
        );
        if (res.data.success) setPrefixPeek(res.data.data);
        else setPrefixPeek(null);
      } catch (error) {
        setPrefixPeek(null);
      }
    }, 400);
    return () => clearTimeout(t);
  }, [
    storeId,
    settings.prefix,
    settings.use_prefix,
    savedPrefix,
    savedUsePrefix,
    currentStatus,
    currentFyId,
  ]);

  const fetchAccounts = useCallback(async () => {
    if (storeId === undefined || storeId === null) return;
    setAccountsLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/accounts/accounts/${storeId}/accounts-only`,
      );
      if (response.data.success) setAccounts(response.data.data);
    } catch (error) {
      console.error("Error fetching accounts:", error);
    } finally {
      setAccountsLoading(false);
    }
  }, [storeId]);

  useEffect(() => {
    if (!modeLoaded || storeId === undefined || storeId === null) return;
    const loadData = async () => {
      await fetchAccounts();
      const { branchFyId, calendarFyId } = await fetchFinancialYears();
      setCalendarFyId(calendarFyId);

      if (currentStatus === 1) {
        setCurrentFyId(null);
        await fetchSettings();
      } else {
        // What this branch chose, and only then the calendar year. Reading
        // is_active first was the bug: it was one flag shared by every store, so
        // whichever branch touched the picker last decided for all of them.
        setCurrentFyId(branchFyId ?? calendarFyId ?? null);
        await fetchSettings();
      }
    };
    loadData();
    // Re-runs on a branch change too: the year belongs to the branch, and
    // switchBranch does not reload the page, so nothing else would re-ask.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modeLoaded, storeId, branchId, currentStatus]);

  const handleFyChange = async (event) => {
    const fyId = parseInt(event.target.value, 10);
    const previousFyId = currentFyId;
    setCurrentFyId(fyId);

    // Roll back the picker if the write failed, rather than showing a year the
    // branch is not actually in.
    const saved = await setActiveFY(fyId);
    if (!saved) {
      setCurrentFyId(previousFyId);
      return;
    }
    await fetchSettings();
  };

  const handleSaveSettings = async () => {
    if (!storeId) return;
    if (!canEditSettings) {
      showSnackbar(VIEW_ONLY_MESSAGE, "error");
      return;
    }
    if (currentStatus === 2 && currentFyId === null) {
      showSnackbar("Please select a financial year first", "error");
      return;
    }
    setLoading(true);
    try {
      const payload = {
        prefix: settings.prefix || null,
        use_prefix: settings.use_prefix ? 1 : 0,
        master_int: settings.master_int,
        master_ins: settings.master_ins === 0 ? null : settings.master_ins,
        OTP_for_cust: settings.OTP_for_cust ? 1 : 0,
        allow_duplicate_mobile: settings.allow_duplicate_mobile ? 1 : 0,
        OTP_for_settlement: settings.OTP_for_settlement ? 1 : 0,
        manual_voucher_no: settings.manual_voucher_no ? 1 : 0,
        one_month_deduct: settings.one_month_deduct ? 1 : 0,
        pledge_acc: settings.pledge_acc,
        cash_acc: settings.cash_acc,
        UPI_acc: settings.UPI_acc,
        interest_acc: settings.interest_acc,
        insurance_acc: settings.insurance_acc,
        other_charges_acc: settings.other_charges_acc,
        no_interest_upto_2_days: settings.no_interest_upto_2_days ? 1 : 0,
        pawn_monthly_interest: settings.pawn_monthly_interest ? 1 : 0,
        pawn_compound_interest: settings.pawn_compound_interest ? 1 : 0,
        less_than_15_days_as_full_month:
          settings.less_than_15_days_as_full_month ? 1 : 0,
        less_than_15_days_as_half_month:
          settings.less_than_15_days_as_half_month ? 1 : 0,
        // Only relevant when switching to a brand-new prefix: restart at 1
        // (true) or continue the running number (false). Ignored otherwise.
        reset_sequence: resetSequence,
        // No auth middleware on this route — pass the acting user's id so the
        // backend can stamp updated_by.
        user_id: userId,
      };
      const response = await axios.post(
        `${API_BASE_URL}/api/pawn/settings/${storeId}`,
        payload,
      );

      if (response.data.success) {
        showSnackbar("Settings saved successfully", "success");
        await fetchSettings();
      } else {
        // Backend returned an error (e.g., statusCode: 500)
        showSnackbar(
          response.data.statusMessage || "Failed to save settings",
          "error",
        );
      }
    } catch (error) {
      // Network or other unexpected error
      showSnackbar(
        error.response?.data?.statusMessage || "Failed to save settings",
        "error",
      );
    } finally {
      setLoading(false);
    }
  };

  const modeColor = isUnofficial ? UNOFFICIAL_COLOR : OFFICIAL_COLOR;
  const currentFyLabel = fyOptions.find(
    (fy) => fy.fy_id === currentFyId,
  )?.fy_label;

  if (!modeLoaded) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "100%",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        p: { xs: 2, sm: 2.5 },
        bgcolor: BG_PAGE,
        // From lg up the page is a flex column that fills what App.js's <main>
        // hands it: header on top, quadrant grid taking the rest, and nothing
        // here scrolls — each quadrant scrolls its own content instead.
        //
        // Below lg it goes back to a plain growing page and <main> does the
        // scrolling, which is why the height and overflow are breakpoint-scoped
        // rather than absolute.
        height: { xs: "auto", lg: "100%" },
        minHeight: { xs: "100%", lg: 0 },
        display: "flex",
        flexDirection: "column",
        overflow: { xs: "visible", lg: "hidden" },
      }}
    >
      {/* ── Top header bar ──────────────────────────────────────── */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 2,
          mb: { xs: 2, lg: 2.5 },
          flexShrink: 0,
        }}
      >
        {/* Title + mode badge */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <PawnNavButtons />
          <Box>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: "#1a1a2e",
                letterSpacing: "-0.3px",
              }}
            >
              Pawn Settings
            </Typography>
            {currentFyLabel && (
              <Typography variant="caption" sx={{ color: "#64748b" }}>
                Financial Year: {currentFyLabel}
              </Typography>
            )}
          </Box>
        </Box>

        {/* Save button — Store Staff get a read-only notice in its place. */}
        {!canEditSettings ? (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.5,
              py: 0.75,
              borderRadius: "10px",
              bgcolor: "#F1F5F9",
              border: "1px solid #E2E8F0",
              color: "#475569",
              fontSize: "0.82rem",
              fontWeight: 600,
            }}
          >
            <LockRoundedIcon sx={{ fontSize: 16 }} />
            {VIEW_ONLY_MESSAGE}
          </Box>
        ) : (
          <Button
            variant="contained"
            startIcon={
              loading ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <SaveIcon />
              )
            }
            onClick={handleSaveSettings}
            disabled={loading}
            sx={{
              bgcolor: "#2563EB",
              color: "#fff",
              fontWeight: 600,
              px: 3,
              py: 1,
              borderRadius: "10px",
              textTransform: "none",
              fontSize: "0.9rem",
              boxShadow: "none",
              "&:hover": { bgcolor: "#1D4ED8", boxShadow: "none" },
            }}
          >
            {loading ? "Saving…" : "Save"}
          </Button>
        )}
      </Box>

      {/* ── Content: four quadrants of one non-scrolling page ─────
          Reading order is the order a store is set up in — identity and
          numbering, then where the money is posted, then how each transaction
          behaves, then what it is charged. */}
      <ViewOnlyZone readOnly={!canEditSettings} sx={QUADRANT_GRID_SX}>
        {/* ┌ top-left ─ Global Master */}
        <Box sx={QUADRANT_SX}>
          <PawnConfiguration
            section="general"
            fetching={fetching}
            settings={settings}
            onSettingsChange={setSettings}
            accounts={accounts}
            accountsLoading={accountsLoading}
            currentStatus={currentStatus}
            currentFyId={currentFyId}
            calendarFyId={calendarFyId}
            fyOptions={fyOptions}
            fyLoading={fyLoading}
            onFyChange={handleFyChange}
            prefixPeek={prefixPeek}
            resetSequence={resetSequence}
            onResetSequenceChange={setResetSequence}
          />
        </Box>

        {/* top-right ─ Account Mapping ┐ */}
        <Box sx={QUADRANT_SX}>
          <PawnConfiguration
            section="accounts"
            fetching={fetching}
            settings={settings}
            onSettingsChange={setSettings}
            accounts={accounts}
            accountsLoading={accountsLoading}
            currentStatus={currentStatus}
          />
        </Box>

        {/* └ bottom-left ─ Payment, Receipt & Settlement Rules */}
        <Box sx={QUADRANT_SX}>
          <PawnConfiguration
            section="rules"
            fetching={fetching}
            settings={settings}
            onSettingsChange={setSettings}
            accounts={accounts}
            accountsLoading={accountsLoading}
            currentStatus={currentStatus}
          />
        </Box>

        {/* bottom-right ─ Interest Slab ┘
            Saves itself: the basis and its slabs are one decision, written
            together through a different endpoint than the rest of this page. */}
        <Box sx={QUADRANT_SX}>
          <PawnInterestSlabs
            storeId={storeId}
            currentStatus={currentStatus}
            userId={userId}
          />
        </Box>
      </ViewOnlyZone>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          severity={snackbar.severity}
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
          sx={{ borderRadius: "10px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default PawnSettings;
